/* Hook managing AI summary generation, backend persistence, style, collapse, and hover state. */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAction, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { AISummaryResult } from "../../convex/aiSummary";
import {
  LOCALSTORAGE_KEYS,
  type SummaryStyle,
} from "@/components/ai-summary/summaryStyleOptions";

const HOVER_LEAVE_DELAY_MS = 150;

/** Returns "YYYY-MM-DD" in the user's local timezone (not UTC). */
function getLocalDateString(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function readLocalBoolean(key: string, fallback: boolean): boolean {
  try {
    const val = localStorage.getItem(key);
    if (val === "true") return true;
    if (val === "false") return false;
  } catch {
    /* ignore */
  }
  return fallback;
}

function readSavedStyle(): SummaryStyle {
  try {
    const val = localStorage.getItem(LOCALSTORAGE_KEYS.style);
    if (val === "concise" || val === "regular" || val === "comprehensive")
      return val;
  } catch {
    /* ignore */
  }
  return "regular";
}

export interface UseAISummaryReturn {
  summary: AISummaryResult | null;
  isLoading: boolean;
  error: string | null;
  summaryStyle: SummaryStyle;
  isCollapsed: boolean;
  showBreakdown: boolean;
  excludeReels: boolean;
  hoveredSegmentKey: string | null;
  breakdownData: ReturnType<typeof useQuery<typeof api.feedItems.getFeedStatsBreakdown>> | undefined;
  setSummaryStyle: (style: SummaryStyle) => void;
  toggleCollapsed: () => void;
  toggleBreakdown: () => void;
  toggleExcludeReels: () => void;
  generateSummary: () => void;
  setHoveredSegmentKey: (key: string | null) => void;
}

export function useAISummary(feedItemCount: number): UseAISummaryReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summaryStyle, setSummaryStyleState] = useState<SummaryStyle>(readSavedStyle);
  const [isCollapsed, setIsCollapsed] = useState(() => readLocalBoolean(LOCALSTORAGE_KEYS.collapsed, false));
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [excludeReels, setExcludeReels] = useState(() => readLocalBoolean(LOCALSTORAGE_KEYS.excludeReels, true));
  const [hoveredSegmentKey, setHoveredSegmentKeyState] = useState<string | null>(null);

  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const generateSummaryAction = useAction(api.aiSummary.generateSummary);

  // Backend-stored summary (reactive — auto-updates when DB changes)
  const latestSummaryRow = useQuery(api.aiSummary.getLatest);
  const latestUpdateStartedAt = useQuery(api.aiSummary.getLatestUpdateStartedAt);

  const breakdownData = useQuery(
    api.feedItems.getFeedStatsBreakdown,
    showBreakdown ? { excludeReels } : "skip",
  );

  // Convert DB row to AISummaryResult shape
  const summary: AISummaryResult | null = useMemo(() => {
    if (!latestSummaryRow) return null;
    const today = getLocalDateString();
    if (latestSummaryRow.date !== today) return null;
    return {
      success: !latestSummaryRow.error,
      date: latestSummaryRow.date,
      totalItems: latestSummaryRow.totalItems,
      readingTime: latestSummaryRow.readingTime,
      summary: latestSummaryRow.summary,
      summaryPoints: latestSummaryRow.summaryPoints,
      goalSuggestions: latestSummaryRow.goalSuggestions,
      keyTopics: latestSummaryRow.keyTopics ?? [],
      highlights: [],
      error: latestSummaryRow.error,
    };
  }, [latestSummaryRow]);

  // Persist UI-only state to localStorage
  useEffect(() => {
    localStorage.setItem(LOCALSTORAGE_KEYS.collapsed, String(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    localStorage.setItem(LOCALSTORAGE_KEYS.style, summaryStyle);
  }, [summaryStyle]);

  useEffect(() => {
    localStorage.setItem(LOCALSTORAGE_KEYS.excludeReels, String(excludeReels));
  }, [excludeReels]);

  const generateSummary = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await generateSummaryAction({
        summaryStyle,
        tagProvocativeContent: true,
        includeGoalSuggestions: true,
        sinceTimestamp: latestUpdateStartedAt ?? undefined,
        localDate: getLocalDateString(),
      });
      if (result.error) {
        setError(result.error);
      }
      // No need to set summary state — Convex reactivity will update latestSummaryRow
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to generate summary";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [generateSummaryAction, summaryStyle, isLoading, latestUpdateStartedAt]);

  // No auto-generate on mount — user clicks "Generate" button instead

  const setSummaryStyle = useCallback((style: SummaryStyle) => {
    setSummaryStyleState(style);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const toggleBreakdown = useCallback(() => {
    setShowBreakdown((prev) => !prev);
  }, []);

  const toggleExcludeReels = useCallback(() => {
    setExcludeReels((prev) => !prev);
  }, []);

  const setHoveredSegmentKey = useCallback((key: string | null) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (key === null) {
      hoverTimeoutRef.current = setTimeout(() => {
        setHoveredSegmentKeyState(null);
      }, HOVER_LEAVE_DELAY_MS);
    } else {
      setHoveredSegmentKeyState(key);
    }
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Suppress unused var warning — feedItemCount kept in interface for future use
  void feedItemCount;

  return {
    summary,
    isLoading,
    error,
    summaryStyle,
    isCollapsed,
    showBreakdown,
    excludeReels,
    hoveredSegmentKey,
    breakdownData,
    setSummaryStyle,
    toggleCollapsed,
    toggleBreakdown,
    toggleExcludeReels,
    generateSummary,
    setHoveredSegmentKey,
  };
}
